import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';

/**
 * Real-backend KYC surface (v1.8.0) — investor submission plus admin
 * review. Supersedes the old mock-backed slice (full_name/bvn/address
 * fields, a single `/kyc` GET, PATCH-based approve/reject) — none of that
 * matched the real API, which is document_type + 1-2 files, POST-based
 * review actions, and no "get my own submission" endpoint at all (see
 * pages/investor/KycForm.jsx for how status-awareness is handled without
 * one).
 *
 * GET /admin/kyc was confirmed live (DevTools) to return a Laravel
 * paginator, not the flat `{ submissions: [...] }` the original Swagger
 * example implied:
 *   { status: true, data: { current_page, data: [...], total, per_page,
 *     last_page, next_page_url, prev_page_url, ... } }
 * The array lives at `response.data.data`. Every parsing helper below is
 * written to never *throw* on an unexpected shape — a shape mismatch on a
 * successful HTTP response is a parsing bug, not a network failure, and
 * throwing inside transformResponse made the two indistinguishable to the
 * UI (see unwrapSubmissions' `parseError` flag and the console.error calls
 * — that's the fix for that specific failure mode, not just the shape
 * itself).
 */
function normalizeKycUserSummary(raw) {
  if (!raw) return null;
  return {
    id: raw.uuid ?? raw.id,
    full_name: [raw.first_name, raw.last_name].filter(Boolean).join(' ') || raw.email,
    email: raw.email ?? null,
    phone: raw.phone ?? null,
  };
}

function normalizeKycDocument(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    original_name: raw.original_name ?? raw.filename ?? 'document',
    mime_type: raw.mime_type ?? null,
    size: Number(raw.size ?? 0),
    download_url: raw.download_url ?? raw.url ?? null,
    created_at: raw.created_at ?? null,
    // v1.18.0 — "other" (non-identity) documents carry a `tag` identifying
    // which checklist item they satisfy (e.g. 'fixed_income_agreement').
    // null on identity-submission documents, which have no tag.
    tag: raw.tag ?? null,
  };
}

// v1.18.0 — a single row in the investor's "Other Documents" checklist,
// from GET /investor/kyc. Each tag with a current template becomes one
// checklist entry; `status` is 'missing' until the investor uploads
// something against that tag, then 'uploaded' — there's no review step for
// these (unlike identity), so 'uploaded' is a terminal state, not pending.
function normalizeOtherDocumentItem(raw) {
  if (!raw) return raw;
  return {
    tag: raw.tag ?? null,
    label: raw.label ?? raw.name ?? raw.tag ?? 'Document',
    status: raw.status ?? (raw.document || raw.uploaded_document ? 'uploaded' : 'missing'),
    document: normalizeKycDocument(raw.document ?? raw.uploaded_document ?? null),
    templateAvailable: raw.template_available ?? raw.has_template ?? true,
    uploadedAt: raw.uploaded_at ?? raw.document?.created_at ?? null,
  };
}

// v1.18.0 — completion breakdown from GET /investor/kyc. Per spec: identity
// is worth 50% (only counted on approval), the remaining 50% is split
// evenly across "other" tags that currently have a template (counted on
// upload, no review needed) — or identity alone is worth 100% if no
// "other" templates exist at all. The percent itself is trusted from the
// server when present; if it's missing this falls back to computing it
// client-side from the same rule so the progress bar never just goes blank
// on a response that omits the precomputed number.
function normalizeKycProgress(raw, { identityApproved, otherItems } = {}) {
  const items = otherItems ?? [];
  let computedPercent;
  if (items.length === 0) {
    computedPercent = identityApproved ? 100 : 0;
  } else {
    const identityShare = identityApproved ? 50 : 0;
    const perTag = 50 / items.length;
    const otherShare = items.filter((i) => i.status === 'uploaded').length * perTag;
    computedPercent = Math.round(identityShare + otherShare);
  }

  return {
    percent: Number.isFinite(raw?.percent) ? raw.percent : computedPercent,
    identity: {
      status: raw?.identity?.status ?? (identityApproved ? 'approved' : 'pending'),
      weight: raw?.identity?.weight ?? (items.length === 0 ? 100 : 50),
    },
  };
}

// v1.18.0 — lightweight entry for GET /investor/kyc/templates (download
// list) — deliberately not the full admin template shape (no version
// history, no soft-delete state) since investors only ever see "the
// current version," never anything about how it got there.
function normalizeInvestorTemplate(raw) {
  if (!raw) return raw;
  return {
    tag: raw.tag ?? null,
    label: raw.label ?? raw.name ?? raw.tag ?? 'Template',
    description: raw.description ?? null,
    downloadUrl: raw.download_url ?? raw.url ?? null,
    updatedAt: raw.updated_at ?? raw.created_at ?? null,
  };
}

// Shared shape for a submission whether it comes from the investor's own
// POST, the admin list, or the admin detail view.
function normalizeSubmission(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    user: normalizeKycUserSummary(raw.user),
    document_type: raw.document_type ?? null,
    status: raw.status ?? 'pending',
    rejection_reason: raw.rejection_reason ?? null,
    reviewer: normalizeKycUserSummary(raw.reviewer),
    reviewed_at: raw.reviewed_at ?? null,
    documents: (raw.documents ?? []).map(normalizeKycDocument),
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
  };
}

function emptyPage() {
  return { items: [], meta: { currentPage: 1, lastPage: 1, perPage: 0, total: 0 } };
}

// GET /admin/kyc — confirmed shape is a Laravel paginator under `data`.
// Never throws: an unrecognized shape logs a console.error and returns an
// empty page tagged with `parseError: true` so the UI can show an honest
// "something went wrong parsing this" message instead of either crashing
// or silently rendering "no submissions" for what might actually be a
// broken response.
function unwrapSubmissionsPage(response) {
  const paginator = response?.data;
  if (paginator && Array.isArray(paginator.data)) {
    return {
      items: paginator.data,
      meta: {
        currentPage: paginator.current_page ?? 1,
        lastPage: paginator.last_page ?? 1,
        perPage: paginator.per_page ?? paginator.data.length,
        total: paginator.total ?? paginator.data.length,
      },
    };
  }

  // Fallback: some deployments/environments might genuinely return a flat
  // array or the originally-documented `{ submissions: [...] }` shape —
  // handle both without assuming pagination exists.
  if (Array.isArray(response)) {
    const items = response;
    return { items, meta: { currentPage: 1, lastPage: 1, perPage: items.length, total: items.length } };
  }
  if (Array.isArray(response?.submissions)) {
    const items = response.submissions;
    return { items, meta: { currentPage: 1, lastPage: 1, perPage: items.length, total: items.length } };
  }

  // eslint-disable-next-line no-console
  console.error(
    '[kycApi] Unexpected GET /admin/kyc response shape — expected a Laravel paginator under `data.data`. Got:',
    response
  );
  return { ...emptyPage(), parseError: true };
}

// Single-resource responses (detail/approve/reject/submit) — Laravel's
// usual `{ status, message, data: {...} }` wrapper for a single object,
// with `submission` and a bare object kept as fallbacks in case a given
// route doesn't follow that convention.
function unwrapSubmission(response) {
  const candidate = response?.submission ?? response?.data ?? response;
  const looksLikeSubmission =
    candidate && typeof candidate === 'object' && ('status' in candidate || 'document_type' in candidate || 'uuid' in candidate || 'id' in candidate);

  if (!looksLikeSubmission) {
    // eslint-disable-next-line no-console
    console.error('[kycApi] Unexpected single-submission response shape. Got:', response);
    return null;
  }
  return candidate;
}

// GET /investor/kyc — v1.18.0's unified status screen. Response shape is
// our best-effort construction from the spec (the live API/mockup were
// both unreachable this session — see kycTemplatesApi.js header for the
// same caveat) modeled after this file's existing `{ data: {...} }`
// convention: `{ data: { identity: {...submission-shaped...},
// other_documents: [...], kyc_progress: {...} } }`. Never throws — an
// unrecognized shape logs and returns a safe empty structure so the screen
// degrades to "nothing submitted yet" instead of crashing.
function unwrapInvestorKyc(response) {
  const body = response?.data ?? response;
  if (!body || typeof body !== 'object') {
    // eslint-disable-next-line no-console
    console.error('[kycApi] Unexpected GET /investor/kyc response shape. Got:', response);
    return { identity: null, other_documents: [], kyc_progress: null };
  }
  return {
    identity: body.identity ?? body.submission ?? null,
    other_documents: Array.isArray(body.other_documents) ? body.other_documents : [],
    kyc_progress: body.kyc_progress ?? body.progress ?? null,
  };
}

export const kycApi = createApi({
  reducerPath: 'kycApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['KycSubmission', 'InvestorKyc', 'InvestorKycTemplate'],
  endpoints: (builder) => ({
    // POST /investor/kyc — multipart/form-data, one document_type + 1-2
    // files under `documents`. 409 means either "already pending" or
    // "already active" (approved accounts can't resubmit) — callers read
    // err.data.message to tell those two apart, since that's the only
    // signal the API gives for which case applies. throttle:10,1 on this
    // route — a 429 is expected under repeated attempts.
    submitKyc: builder.mutation({
      query: (formData) => ({ url: '/investor/kyc', method: 'POST', data: formData }),
      transformResponse: (response) => normalizeSubmission(unwrapSubmission(response)),
      invalidatesTags: [{ type: 'KycSubmission', id: 'LIST' }],
    }),

    // GET /admin/kyc?status=pending|approved|rejected|all&page=N. Always
    // called with an explicit status from the queue's active tab rather
    // than relying on the server's pending default, so the UI and the
    // cache key never disagree about which view is showing. Returns
    // { submissions, meta: {currentPage,lastPage,perPage,total}, parseError }
    // rather than a bare array, since the real response is paginated.
    adminListKyc: builder.query({
      query: ({ status, page } = {}) => ({ url: '/admin/kyc', method: 'GET', params: { status, page } }),
      transformResponse: (response) => {
        const { items, meta, parseError } = unwrapSubmissionsPage(response);
        return { submissions: items.map(normalizeSubmission), meta, parseError: !!parseError };
      },
      providesTags: (result) => [
        ...(result?.submissions ?? []).map(({ id }) => ({ type: 'KycSubmission', id })),
        { type: 'KycSubmission', id: 'LIST' },
      ],
    }),

    // GET /admin/kyc/{submission}
    adminGetKyc: builder.query({
      query: (id) => ({ url: `/admin/kyc/${id}`, method: 'GET' }),
      transformResponse: (response) => normalizeSubmission(unwrapSubmission(response)),
      providesTags: (result, error, id) => [{ type: 'KycSubmission', id }],
    }),

    // POST /admin/kyc/{submission}/approve — no body. 409 if the
    // submission isn't pending anymore (already reviewed) — the UI also
    // disables the button once status !== 'pending' so this is a backstop,
    // not the primary guard.
    approveKyc: builder.mutation({
      query: (id) => ({ url: `/admin/kyc/${id}/approve`, method: 'POST' }),
      transformResponse: (response) => normalizeSubmission(unwrapSubmission(response)),
      invalidatesTags: (result, error, id) => [
        { type: 'KycSubmission', id },
        { type: 'KycSubmission', id: 'LIST' },
      ],
    }),

    // POST /admin/kyc/{submission}/reject — the docs require a rejection
    // reason but don't name the body field; using `rejection_reason` since
    // that's the exact field name the submission schema itself uses for
    // this value everywhere it appears in responses.
    rejectKyc: builder.mutation({
      query: ({ id, reason }) => ({ url: `/admin/kyc/${id}/reject`, method: 'POST', data: { rejection_reason: reason } }),
      transformResponse: (response) => normalizeSubmission(unwrapSubmission(response)),
      invalidatesTags: (result, error, { id }) => [
        { type: 'KycSubmission', id },
        { type: 'KycSubmission', id: 'LIST' },
      ],
    }),

    // GET /investor/kyc — v1.18.0. Combines identity submission status with
    // the new "Other Documents" checklist and completion progress into one
    // screen, replacing the old account_status-inference dance in
    // pages/investor/KycForm.jsx with a real GET.
    getInvestorKyc: builder.query({
      query: () => ({ url: '/investor/kyc', method: 'GET' }),
      transformResponse: (response) => {
        const { identity, other_documents, kyc_progress } = unwrapInvestorKyc(response);
        const normalizedIdentity = identity ? normalizeSubmission(identity) : null;
        const otherItems = other_documents.map(normalizeOtherDocumentItem);
        return {
          identity: normalizedIdentity,
          otherDocuments: otherItems,
          kycProgress: normalizeKycProgress(kyc_progress, {
            identityApproved: normalizedIdentity?.status === 'approved',
            otherItems,
          }),
        };
      },
      providesTags: ['InvestorKyc'],
    }),

    // GET /investor/kyc/templates — download list for whatever "other"
    // tags currently have a template. Kept as its own endpoint/tag (rather
    // than folded into getInvestorKyc) since a template changing never
    // invalidates the investor's own progress/checklist per the spec's
    // explicit "never prompt to resubmit" rule — the two really are
    // independent pieces of state.
    listInvestorKycTemplates: builder.query({
      query: () => ({ url: '/investor/kyc/templates', method: 'GET' }),
      transformResponse: (response) => {
        const list = response?.data ?? response;
        const items = Array.isArray(list) ? list : [];
        return items.map(normalizeInvestorTemplate);
      },
      providesTags: ['InvestorKycTemplate'],
    }),

    // POST /investor/kyc/documents — multipart: `tag` + a single `document`
    // file. Doubles as re-upload/replace (per spec, "other" documents have
    // no review step to block a re-submit the way identity does) — the
    // investor-side UI always shows this as "Upload" or "Replace" against
    // the same endpoint, never a separate replace route.
    uploadKycDocument: builder.mutation({
      query: (formData) => ({ url: '/investor/kyc/documents', method: 'POST', data: formData }),
      invalidatesTags: ['InvestorKyc'],
    }),
  }),
});

export const {
  useSubmitKycMutation,
  useAdminListKycQuery,
  useAdminGetKycQuery,
  useApproveKycMutation,
  useRejectKycMutation,
  useGetInvestorKycQuery,
  useListInvestorKycTemplatesQuery,
  useUploadKycDocumentMutation,
} = kycApi;

// Exported for reuse by adminInvestorsApi.js (Investor Detail's kyc_progress
// + documents fields reuse the exact same shape/rules rather than
// duplicating the completion-percent math in a second place).
export { normalizeKycDocument, normalizeOtherDocumentItem, normalizeKycProgress, normalizeSubmission };
